import { TicketOptionWithQuantity, ProductInstance, Translations } from '@repo/types';
import { QuantityError, ValidationContext, QuantityInputItem } from '../shared/types';
import {
    validateIndividualMinEntrants,
    validateIndividualCapacity,
    validateSharedInstanceMinEntrants,
    validateSharedProductInstanceCapacity,
    validateNotEmpty,
} from '../shared/validation-rules';
import { calculateBaseMax, calculateMin } from '../shared/capacity-calculators';
import {
    getTicketName,
    getAgeRangeText,
    getErrorForTicket,
    capitalize,
} from '../shared/transformers';

export const buildPackageContext = (
    tickets: TicketOptionWithQuantity[],
    selectedProducts: ProductInstance[],
): ValidationContext => {
    return {
        hasCollectionMode: false,
        hasJoinedTrip: false,
        requireAllValid: false,
        shouldValidateIndividual: true,
        maxEntrantsPerBooking: null,
        globalMaxEntrants: null,
        collectionMinEntrants: null,
        currentConsumedCapacity: 0,
        sharedResourceCapacity: null,
        productInstances: selectedProducts,
    };
};

export const validatePackageQuantities = (
    tickets: TicketOptionWithQuantity[],
    context: ValidationContext,
    attemptedBooking = false,
): QuantityError[] => {
    const errors: QuantityError[] = [];

    errors.push(...validateNotEmpty(tickets, attemptedBooking));

    for (const ticket of tickets) {
        errors.push(...validateIndividualMinEntrants(ticket, context));
        errors.push(...validateIndividualCapacity(ticket, context));
    }

    // Shared product instance validation (TOTAL-based for minEntrants and capacity)
    errors.push(...validateSharedInstanceMinEntrants(tickets, context));
    errors.push(...validateSharedProductInstanceCapacity(tickets, context));

    return errors;
};

export const calculatePackageMax = (
    ticket: TicketOptionWithQuantity,
    _allTickets: TicketOptionWithQuantity[],
    _context: ValidationContext,
): number => {
    return calculateBaseMax(ticket);
};

export const calculatePackageMin = (ticket: TicketOptionWithQuantity): number => {
    return calculateMin(ticket);
};

export const updatePackageQuantity = (
    tickets: TicketOptionWithQuantity[],
    ticketCategoryId: string,
    newQuantity: number,
): TicketOptionWithQuantity[] => {
    return tickets.map((ticket) =>
        ticket.ticketCategoryId === ticketCategoryId
            ? { ...ticket, quantity: newQuantity }
            : ticket,
    );
};

export const toPackageInputItems = (
    tickets: TicketOptionWithQuantity[],
    context: ValidationContext,
    translations: Translations,
    errors?: QuantityError[],
): QuantityInputItem[] => {
    const validationErrors = errors ?? validatePackageQuantities(tickets, context, false);

    return tickets.map((ticket) => ({
        id: ticket.ticketCategoryId,
        label: capitalize(getTicketName(ticket, translations)),
        value: ticket.quantity,
        min: 0, // Always allow reducing to 0 - validation will show errors if needed
        max: calculatePackageMax(ticket, tickets, context),
        subText: getAgeRangeText(ticket, translations),
        error: getErrorForTicket(ticket.ticketCategoryId, validationErrors, ticket, translations),
    }));
};
